import React, { useState, useEffect } from 'react';
import { X, User, Mail, Phone, Tv, Server, Key, Calendar, AlertCircle, CheckCircle, CreditCard, Send, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../services/api';

const ClientEditModal = ({ client, onClose, onSave, isAdmin = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clientDetails, setClientDetails] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sendingPaymentLink, setSendingPaymentLink] = useState(false);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(59);
  const [paymentPlan] = useState('Abonnement IPTV 12 mois');
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    device_type: '',
    iptv_app: '',
    mac_address: '',
    device_id: '',
    snapchat: ''
  });

  useEffect(() => {
    fetchClientDetails();
  }, [client.id]);

  const fetchClientDetails = async () => {
    try {
      const endpoint = isAdmin 
        ? `/admin/users/${client.id}/details`
        : `/vendors/clients/${client.id}/details`;
      const response = await api.get(endpoint);
      setClientDetails(response.data);
      setFormData({
        full_name: response.data.full_name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        device_type: response.data.device_type || '',
        iptv_app: response.data.iptv_app || '',
        mac_address: response.data.mac_address || '',
        device_id: response.data.device_id || '',
        snapchat: response.data.snapchat || ''
      });
    } catch (err) {
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveInfo = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const endpoint = isAdmin 
        ? `/admin/users/${client.id}`
        : `/vendors/clients/${client.id}`;
      await api.put(endpoint, formData);
      setSuccess('Informations mises à jour avec succès');
      setTimeout(() => {
        setSuccess('');
        if (onSave) onSave();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleSendPaymentLink = async () => {
    setSendingPaymentLink(true);
    setError('');
    setSuccess('');
    
    try {
      const endpoint = isAdmin ? '/admin/payment-links' : '/vendors/payment-links';
      await api.post(endpoint, {
        email: formData.email,
        full_name: formData.full_name,
        amount: paymentAmount,
        plan_name: paymentPlan
      });
      setSuccess(`Lien de paiement envoyé à ${formData.email} !`);
      setShowPaymentOptions(false);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi du lien');
    } finally {
      setSendingPaymentLink(false);
    }
  };

  const hasActiveSubscription = clientDetails?.subscription?.status === 'active';
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getSubscriptionLabel = () => {
    if (!clientDetails?.subscription || clientDetails?.subscription?.status !== 'active') {
      return { text: "Pas d'accès", color: 'text-[#FF3B5C]', bg: 'bg-[#FF3B5C]/10' };
    }
    const endDate = formatDate(clientDetails.subscription.end_date);
    return { 
      text: `Accès actif jusqu'au ${endDate}`, 
      color: 'text-[#00FF88]', 
      bg: 'bg-[#00FF88]/10' 
    };
  };

  const subscriptionLabel = getSubscriptionLabel();

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#1A1A1D] rounded-lg metallic-border w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="client-edit-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#3A3A3D]">
          <h2 className="text-2xl font-bold text-white">Modifier le client</h2>
          <button onClick={onClose} className="text-[#B8B8BA] hover:text-white">
            <X className="h-6 w-6" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#00FFFF] border-t-transparent mx-auto" />
            <p className="text-[#B8B8BA] mt-4">Chargement...</p>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Status Badge */}
            <div className="flex items-center justify-between">
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${subscriptionLabel.bg} ${subscriptionLabel.color}`}>
                {subscriptionLabel.text}
              </span>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                clientDetails?.is_active ? 'bg-[#00FFFF]/10 text-[#00FFFF]' : 'bg-[#FF3B5C]/10 text-[#FF3B5C]'
              }`}>
                {clientDetails?.is_active ? 'Compte actif' : 'Compte désactivé'}
              </span>
            </div>

            {/* Payment Link Quick Action */}
            <div className="bg-gradient-to-r from-[#00FFFF]/10 to-[#00FF88]/10 p-4 rounded-lg border border-[#00FFFF]/30">
              {!showPaymentOptions ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <CreditCard className="h-6 w-6 text-[#00FFFF]" />
                    <div>
                      <p className="text-white font-medium">Envoyer un lien de paiement</p>
                      <p className="text-[#6B6B6E] text-sm">Le client recevra un email avec un lien pour payer</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowPaymentOptions(true)}
                    className="btn-primary py-2 px-4 text-sm flex items-center space-x-2"
                    data-testid="send-payment-link-btn"
                  >
                    <Send className="h-4 w-4" />
                    <span>Envoyer</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-white font-semibold">Options du lien de paiement</h4>
                    <button onClick={() => setShowPaymentOptions(false)} className="text-[#6B6B6E] hover:text-white">
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  
                  {/* Fixed plan display */}
                  <div className="bg-[#1A1A1D] p-3 rounded-lg">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-[#6B6B6E] text-xs">Offre</span>
                        <div className="text-white font-medium text-sm">{paymentPlan}</div>
                      </div>
                      <div className="text-[#00FFFF] font-bold">59€/an</div>
                    </div>
                  </div>
                  
                  {/* Custom amount input */}
                  <div>
                    <label className="block text-[#B8B8BA] text-sm mb-1">Montant personnalisé (€)</label>
                    <input
                      type="number"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(parseFloat(e.target.value) || 0)}
                      className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-3 py-2 text-white text-sm"
                      min="0"
                      step="0.01"
                    />
                    <p className="text-[#6B6B6E] text-xs mt-1">Modifiez si réduction spéciale</p>
                  </div>
                  
                  <button
                    onClick={handleSendPaymentLink}
                    disabled={sendingPaymentLink}
                    className="btn-primary w-full py-2 flex items-center justify-center space-x-2 disabled:opacity-50"
                  >
                    {sendingPaymentLink ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Envoi en cours...</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        <span>Envoyer le lien ({paymentAmount}€)</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Messages */}
            {error && (
              <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C]/30 text-[#FF3B5C] p-4 rounded-lg flex items-center space-x-3">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            
            {success && (
              <div className="bg-[#00FF88]/10 border border-[#00FF88]/30 text-[#00FF88] p-4 rounded-lg flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 flex-shrink-0" />
                <span>{success}</span>
              </div>
            )}

            {/* Client Info Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                <User className="h-5 w-5 text-[#00FFFF]" />
                <span>Informations client</span>
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#B8B8BA] mb-2 text-sm">Nom complet</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[#B8B8BA] mb-2 text-sm">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[#B8B8BA] mb-2 text-sm">Téléphone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[#B8B8BA] mb-2 text-sm">Type d'appareil</label>
                  <select
                    value={formData.device_type}
                    onChange={(e) => setFormData({...formData, device_type: e.target.value})}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                    data-testid="edit-device-type"
                  >
                    <option value="">Sélectionner...</option>
                    <option value="smart_tv">Smart TV</option>
                    <option value="android_box">Box Android</option>
                    <option value="firestick">Fire Stick</option>
                    <option value="smartphone">Smartphone</option>
                    <option value="tablet">Tablette</option>
                    <option value="other">Autre</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#B8B8BA] mb-2 text-sm">Application IPTV</label>
                  <input
                    type="text"
                    value={formData.iptv_app}
                    onChange={(e) => setFormData({...formData, iptv_app: e.target.value})}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                    placeholder="IPTV Smarters, TiviMate..."
                    data-testid="edit-iptv-app"
                  />
                </div>
                <div>
                  <label className="block text-[#B8B8BA] mb-2 text-sm">Adresse MAC</label>
                  <input
                    type="text"
                    value={formData.mac_address}
                    onChange={(e) => setFormData({...formData, mac_address: e.target.value})}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                    placeholder="AA:BB:CC:DD:EE:FF"
                    data-testid="edit-mac-address"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[#B8B8BA] mb-2 text-sm">Device ID</label>
                <input
                  type="text"
                  value={formData.device_id}
                  onChange={(e) => setFormData({...formData, device_id: e.target.value})}
                  className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                  placeholder="Identifiant de l'appareil"
                  data-testid="edit-device-id"
                />
              </div>

              <div>
                <label className="block text-[#B8B8BA] mb-2 text-sm">Snapchat</label>
                <input
                  type="text"
                  value={formData.snapchat}
                  onChange={(e) => setFormData({...formData, snapchat: e.target.value})}
                  className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                  placeholder="Pseudo Snapchat"
                  data-testid="edit-snapchat"
                />
              </div>
              
              <button
                onClick={handleSaveInfo}
                disabled={saving}
                className="btn-primary w-full disabled:opacity-50"
              >
                {saving ? 'Enregistrement...' : 'Enregistrer les informations'}
              </button>
            </div>

            {/* Note: Gestion IPTV déplacée vers colonne Abonnements */}
            <div className="bg-[#00FFFF]/10 border border-[#00FFFF]/30 p-4 rounded-lg">
              <p className="text-[#00FFFF] text-sm">
                💡 <strong>Gestion des identifiants IPTV :</strong> Cliquez sur les icônes dans la colonne "Abonnements" pour gérer les identifiants et dates d'expiration.
              </p>
            </div>

            {/* Close Button */}
            <div className="pt-4 border-t border-[#3A3A3D]">
              <button
                onClick={onClose}
                className="w-full bg-[#3A3A3D] text-white py-3 rounded-lg hover:bg-[#4A4A4D] transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ClientEditModal;
